import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  type ProposalPayment,
  type ProposalPaymentPayload,
  proposalApi,
} from '@/api/commercial/proposals';
import {
  fmtCents,
  renderDiscountTag,
  SectionTitle,
  SummaryRow,
} from '@/components/commercial/proposals/shared/summary-components';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  isFutureCivilDate,
  type PaymentFormState,
  parseCivilDate,
} from '@/hooks/commercial/use-cart-step-validation';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import { centsToPtbr } from '@/lib/formatters';
import type { ICartItemBreakdown, ICartPaymentSummary } from '@/types/commercial/cart';
import type { CartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';

type PaymentType = 'CASH' | 'INSTALLMENTS' | '';

const PAYMENT_OPTIONS = [
  { value: 'CASH', label: 'À vista' },
  { value: 'INSTALLMENTS', label: 'A prazo' },
] as const;

const INSTALLMENT_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function formatCurrencyInput(digits: string): string {
  if (!digits) return '';
  return centsToPtbr(Number(digits));
}

function dateToIso(date: Date | null): string | null {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parsed = parseCivilDate(iso);
  if (!parsed) return null;
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(parsed.year, parsed.month - 1, parsed.day);
  return date;
}

interface StepCartPaymentPanelProps {
  proposalId?: string;
  savedPayment?: ProposalPayment | null;
  isPickup?: boolean;
  breakdown: ICartItemBreakdown | null;
  totalDiscountInCents: number;
  cartSummaryCategories: CartSummaryCategories;
  paymentSummary: ICartPaymentSummary | null;
  isRecurringOnly: boolean;
  hasCartItems: boolean;
  allowedPaymentMethods?: string[];
  canUseInstallments?: boolean;
  installmentsRestrictionReason?: string | null;
  minEntrancePercent?: number;
  maxInstallments?: number;
  onPaymentStateChange?: (state: PaymentFormState) => void;
  onSavePaymentRef?: (saveFn: (() => Promise<void>) | null) => void;
}

export function StepCartPaymentPanel({
  proposalId,
  savedPayment,
  isPickup,
  breakdown,
  totalDiscountInCents,
  cartSummaryCategories,
  paymentSummary,
  isRecurringOnly,
  hasCartItems,
  allowedPaymentMethods,
  canUseInstallments = true,
  installmentsRestrictionReason,
  minEntrancePercent = 0,
  maxInstallments,
  onPaymentStateChange,
  onSavePaymentRef,
}: StepCartPaymentPanelProps) {
  const queryClient = useQueryClient();

  const [paymentType, setPaymentType] = useState<PaymentType>('');
  const [dueDay, setDueDay] = useState('');
  const [hasEntrance, setHasEntrance] = useState(false);
  const [entrancePercent, setEntrancePercent] = useState('');
  const [freightOnEntrance, setFreightOnEntrance] = useState(true);
  const [installmentCount, setInstallmentCount] = useState('');
  const [freightDigits, setFreightDigits] = useState('');
  const [downPaymentByBankSlip, setDownPaymentByBankSlip] = useState(false);
  const [prorrogarEntrada, setProrrogarEntrada] = useState(false);
  const [prorrogarDate, setProrrogarDate] = useState<Date | null>(null);
  const [boletoAvista, setBoletoAvista] = useState(false);
  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);
  const [entranceError, setEntranceError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const hydratedProposalRef = useRef<string | undefined>(undefined);
  const hydratedPaymentRef = useRef<ProposalPayment | null | undefined>(undefined);
  const editVersionRef = useRef(0);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const queuedVersionRef = useRef<number | null>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const downPaymentByBankSlipId = useId();
  const prorrogarEntradaId = useId();
  const boletoAvistaId = useId();
  const prorrogarDateId = useId();
  const prorrogarDateErrorId = useId();

  const markDirty = useCallback(() => {
    editVersionRef.current += 1;
    setPaymentError(null);
    setEntranceError(null);
    setIsDirty(true);
  }, []);

  useEffect(() => {
    const proposalChanged = hydratedProposalRef.current !== proposalId;
    if (!proposalChanged && (isDirty || hydratedPaymentRef.current === savedPayment)) return;

    hydratedProposalRef.current = proposalId;
    hydratedPaymentRef.current = savedPayment;
    editVersionRef.current += 1;
    setPaymentType(savedPayment?.paymentType ?? '');
    setDueDay(savedPayment?.dueDay ? String(savedPayment.dueDay) : '');
    setHasEntrance(savedPayment?.hasDownPayment ?? false);
    setEntrancePercent(
      savedPayment?.downPaymentPercentage != null ? String(savedPayment.downPaymentPercentage) : '',
    );
    setFreightOnEntrance(savedPayment?.shippingIncludedInDownPayment ?? true);
    setInstallmentCount(savedPayment?.installments ? String(savedPayment.installments) : '');
    setFreightDigits(savedPayment?.freteInCents ? String(savedPayment.freteInCents) : '');
    setDownPaymentByBankSlip(savedPayment?.downPaymentByBankSlip ?? false);
    setProrrogarEntrada(savedPayment?.prorrogarEntrada ?? false);
    setProrrogarDate(isoToDate(savedPayment?.entradaProrrogadaEm));
    setBoletoAvista(savedPayment?.boletoAvista ?? false);
    setIsDirty(false);
    setIsHydrated(!!proposalId);
  }, [proposalId, savedPayment, isDirty]);

  const savePaymentMutation = useMutation({
    mutationFn: ({
      id,
      payment,
    }: {
      id: string;
      payment: ProposalPaymentPayload;
      version: number;
    }) => proposalApi.setPayment(id, payment),
    onSuccess: (updatedProposal, variables) => {
      setEntranceError(null);
      setPaymentError(null);
      queryClient.setQueryData(['proposal', variables.id], updatedProposal);
      queryClient.invalidateQueries({ queryKey: ['cart-calculate', variables.id] });
    },
    onError: (error, variables) => {
      if (variables.version !== editVersionRef.current) return;
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      const serverField = axiosError.response?.data?.error;
      const message = getErrorMessage(error);
      setPaymentError(message);
      if (serverField === 'entradaInCents') {
        setEntranceError(message);
      }
      toast({ title: 'Pagamento não salvo', description: message, variant: 'destructive' });
    },
  });

  const handleSavePayment = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    if (!isDirty) {
      await saveQueueRef.current;
      return;
    }

    if (!proposalId || !paymentType) return;

    const freight = isPickup ? 0 : Number(freightDigits) || 0;
    const entradaPercentVal = Number(entrancePercent) || 0;

    if (hasEntrance && entradaPercentVal <= 0) return;

    const payload: ProposalPaymentPayload = {
      paymentType: paymentType as 'CASH' | 'INSTALLMENTS',
      dueDay: Number(dueDay) || 1,
      installments: Number(installmentCount) || 1,
      freteInCents: freight,
      shippingIncludedInDownPayment: freightOnEntrance,
      hasDownPayment: hasEntrance,
      downPaymentPercentage: hasEntrance ? entradaPercentVal : undefined,
      downPaymentByBankSlip:
        paymentType === 'INSTALLMENTS' && hasEntrance ? downPaymentByBankSlip : false,
      prorrogarEntrada: paymentType === 'INSTALLMENTS' && hasEntrance ? prorrogarEntrada : false,
      entradaProrrogadaEm:
        paymentType === 'INSTALLMENTS' && hasEntrance && prorrogarEntrada
          ? dateToIso(prorrogarDate)
          : null,
      boletoAvista: paymentType === 'CASH' ? boletoAvista : false,
    };

    const version = editVersionRef.current;
    if (queuedVersionRef.current === version) {
      await saveQueueRef.current;
      return;
    }

    queuedVersionRef.current = version;
    const queuedSave = saveQueueRef.current
      .catch(() => undefined)
      .then(() => savePaymentMutation.mutateAsync({ id: proposalId, payment: payload, version }));
    saveQueueRef.current = queuedSave;
    try {
      await queuedSave;
      if (editVersionRef.current === version) setIsDirty(false);
    } finally {
      if (queuedVersionRef.current === version) queuedVersionRef.current = null;
    }
  }, [
    boletoAvista,
    downPaymentByBankSlip,
    dueDay,
    entrancePercent,
    freightDigits,
    freightOnEntrance,
    hasEntrance,
    installmentCount,
    isDirty,
    isPickup,
    paymentType,
    proposalId,
    prorrogarDate,
    prorrogarEntrada,
    savePaymentMutation,
  ]);

  useEffect(() => {
    onSavePaymentRef?.(handleSavePayment);
    return () => onSavePaymentRef?.(null);
  }, [handleSavePayment, onSavePaymentRef]);

  useEffect(() => {
    if (!isDirty) return;
    const preventPendingSaveLoss = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventPendingSaveLoss);
    return () => window.removeEventListener('beforeunload', preventPendingSaveLoss);
  }, [isDirty]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional auto-save trigger
  useEffect(() => {
    if (!proposalId || !paymentType || !hasCartItems || !isHydrated || !isDirty) return;

    if (hasEntrance && minEntrancePercent > 0) {
      const pct = Number(entrancePercent) || 0;
      if (pct > 0 && pct < minEntrancePercent) return;
    }

    if (paymentType === 'INSTALLMENTS' && hasEntrance && prorrogarEntrada) {
      const iso = dateToIso(prorrogarDate);
      if (!iso || !isFutureCivilDate(iso)) return;
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      void handleSavePayment().catch(() => {});
    }, 800);
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [
    paymentType,
    dueDay,
    hasEntrance,
    entrancePercent,
    freightOnEntrance,
    installmentCount,
    freightDigits,
    breakdown,
    minEntrancePercent,
    downPaymentByBankSlip,
    prorrogarEntrada,
    prorrogarDate,
    boletoAvista,
    hasEntrance,
    isDirty,
    isHydrated,
  ]);

  const effectivePaymentMethods = allowedPaymentMethods ?? ['CASH', 'INSTALLMENTS'];
  const isInstallmentsAllowed =
    canUseInstallments && effectivePaymentMethods.includes('INSTALLMENTS');
  const effectiveRestrictionReason =
    installmentsRestrictionReason ??
    'Pagamento a prazo não disponível para este cliente. Utilize o pagamento à vista.';

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional forced reset / auto-select
  useEffect(() => {
    if (!hasCartItems) return;
    const mustUseCash = !isInstallmentsAllowed || isRecurringOnly;
    // Auto-select CASH when installments are restricted and nothing is selected,
    // or reset from INSTALLMENTS if credit analysis no longer allows it.
    if (mustUseCash && paymentType !== 'CASH') {
      setPaymentType('CASH');
      setDownPaymentByBankSlip(false);
      setProrrogarEntrada(false);
      setProrrogarDate(null);
      // The auto-selection must be persisted like a manual selection, otherwise the
      // proposal keeps payment = null and the resume step blocks submission (NO_PAYMENT).
      // Skip when the persisted payment is already CASH (stale closure right after
      // hydration) to avoid a redundant save.
      if (savedPayment?.paymentType !== 'CASH') {
        markDirty();
      }
    }
  }, [hasCartItems, isInstallmentsAllowed, isRecurringOnly, paymentType]);

  useEffect(() => {
    if (!hasCartItems || canUseInstallments) return;
    setRestrictionModalOpen(true);
  }, [canUseInstallments, hasCartItems]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — fire when any field changes
  useEffect(() => {
    onPaymentStateChange?.({
      paymentType,
      dueDay,
      installmentCount,
      hasEntrance,
      entrancePercent,
      downPaymentByBankSlip,
      prorrogarEntrada,
      entradaProrrogadaEm: dateToIso(prorrogarDate),
      boletoAvista,
    });
  }, [
    paymentType,
    dueDay,
    installmentCount,
    hasEntrance,
    entrancePercent,
    downPaymentByBankSlip,
    prorrogarEntrada,
    prorrogarDate,
    boletoAvista,
  ]);

  const basePaymentOptions = PAYMENT_OPTIONS.filter((o) =>
    effectivePaymentMethods.includes(o.value),
  );
  const filteredPaymentOptions = isRecurringOnly
    ? basePaymentOptions.filter((o) => o.value === 'CASH')
    : basePaymentOptions;
  const summary = paymentSummary;

  const freightDisplayValue = summary?.shippingInCents ?? (Number(freightDigits) || 0);
  const hasFreight = freightDisplayValue > 0;
  const isInstallmentPayment = paymentType === 'INSTALLMENTS';

  const hasProducts = (breakdown?.products.totalItems ?? 0) > 0;
  const hasAccessories = (breakdown?.accessories.totalItems ?? 0) > 0;
  const hasConnectivity = (breakdown?.connectivity.totalItems ?? 0) > 0;
  const hasMonthlyServices = (breakdown?.monthlyServices.totalItems ?? 0) > 0;
  const hasYearlyServices = (breakdown?.yearlyServices.totalItems ?? 0) > 0;
  const hasFixedServices = (breakdown?.fixedServices.totalItems ?? 0) > 0;
  const hasAnyItemSection =
    hasProducts ||
    hasAccessories ||
    hasConnectivity ||
    hasMonthlyServices ||
    hasYearlyServices ||
    hasFixedServices;

  const installmentLabel = summary?.installmentTotal
    ? `${hasEntrance ? 'Entrada + ' : ''}${summary.installmentTotal.installments}x`
    : undefined;
  const showTotalParcelado = paymentType === 'INSTALLMENTS' && !!summary?.installmentTotal;
  const showTotalAVista = paymentType === 'CASH' && !!summary?.cashTotal;
  const isFreightInInstallments = isInstallmentPayment && hasFreight && !freightOnEntrance;
  const parceladoComFreteInCents = summary?.installmentTotal
    ? Math.max(
        0,
        summary.installmentTotal.totalInCents - summary.installmentTotal.downPaymentInCents,
      )
    : 0;
  const showRecurringConnectivity =
    (summary?.recurringConnectivity?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringMonthlyServices =
    (summary?.recurringMonthlyServices?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringYearlyServices =
    (summary?.recurringYearlyServices?.yearlyPaymentInCents ?? 0) > 0;
  const prorrogarDateIso = dateToIso(prorrogarDate);
  const hasInvalidProrrogarDate =
    prorrogarEntrada && (!prorrogarDateIso || !isFutureCivilDate(prorrogarDateIso));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Pagamento</CardTitle>
      </CardHeader>

      <CardContent className="space-y-3.5">
        {paymentError &&
          !(
            paymentType === 'INSTALLMENTS' &&
            maxInstallments != null &&
            Number(installmentCount) > maxInstallments
          ) && (
            <div
              role="alert"
              className="space-y-2 rounded-md border border-destructive p-3 text-sm text-destructive"
            >
              <p className="font-medium">Pagamento não salvo</p>
              <p>{paymentError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={savePaymentMutation.isPending}
                onClick={() => void handleSavePayment().catch(() => {})}
              >
                Tentar salvar novamente
              </Button>
            </div>
          )}
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-gray-700">Tipo de pagamento</span>
          <Select
            value={paymentType}
            onValueChange={(v) => {
              if (v === 'INSTALLMENTS' && !isInstallmentsAllowed) {
                setRestrictionModalOpen(true);
                return;
              }
              setPaymentType(v as PaymentType);
              markDirty();
              if (v === 'CASH') {
                setDownPaymentByBankSlip(false);
                setProrrogarEntrada(false);
                setProrrogarDate(null);
              } else {
                setBoletoAvista(false);
              }
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {filteredPaymentOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={restrictionModalOpen} onOpenChange={setRestrictionModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Atenção</DialogTitle>
              <DialogDescription>{effectiveRestrictionReason}</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-gray-700">
              Para continuar com esta proposta, utilize pagamento à vista no carrinho.
            </p>
            <DialogFooter>
              <Button onClick={() => setRestrictionModalOpen(false)}>Entendi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {hasCartItems && (
          <>
            {paymentType === 'INSTALLMENTS' && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-gray-700">Vencimento</span>
                <Select
                  value={dueDay}
                  onValueChange={(value) => {
                    setDueDay(value);
                    markDirty();
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Selecione o dia" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        Dia {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {paymentType === 'INSTALLMENTS' && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">Entrada</span>
                  <Switch
                    checked={hasEntrance}
                    aria-label="Entrada"
                    onCheckedChange={(value) => {
                      setHasEntrance(value);
                      if (!value) {
                        setDownPaymentByBankSlip(false);
                        setProrrogarEntrada(false);
                        setProrrogarDate(null);
                      }
                      markDirty();
                    }}
                  />
                </div>

                {hasEntrance && (
                  <>
                    <div className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-700">
                        Porcentagem de entrada
                      </span>
                      <div className="relative">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          className="h-8 pr-8 text-xs"
                          value={entrancePercent}
                          onChange={(e) => {
                            setEntrancePercent(e.target.value);
                            setEntranceError(null);
                            markDirty();
                          }}
                          placeholder="0"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          %
                        </span>
                      </div>
                      {hasEntrance &&
                        minEntrancePercent > 0 &&
                        (Number(entrancePercent) || 0) > 0 &&
                        (Number(entrancePercent) || 0) < minEntrancePercent && (
                          <p className="text-xs text-destructive">
                            Entrada mínima permitida: {minEntrancePercent}%
                          </p>
                        )}
                      {entranceError &&
                        !(
                          hasEntrance &&
                          minEntrancePercent > 0 &&
                          (Number(entrancePercent) || 0) > 0 &&
                          (Number(entrancePercent) || 0) < minEntrancePercent
                        ) && <p className="text-xs text-destructive">{entranceError}</p>}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Frete na entrada ou 1ª parcela
                      </span>
                      <Switch
                        checked={freightOnEntrance}
                        aria-label="Frete na entrada ou primeira parcela"
                        onCheckedChange={(value) => {
                          setFreightOnEntrance(value);
                          markDirty();
                        }}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-700">Parcelas</span>
                  <Select
                    value={installmentCount}
                    onValueChange={(value) => {
                      setInstallmentCount(value);
                      markDirty();
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {INSTALLMENT_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}x
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {maxInstallments != null && (
                    <p
                      className={
                        Number(installmentCount) > maxInstallments
                          ? 'text-xs text-destructive'
                          : 'text-xs text-muted-foreground'
                      }
                      role={Number(installmentCount) > maxInstallments ? 'alert' : undefined}
                    >
                      {Number(installmentCount) > maxInstallments
                        ? `Não é possível salvar em ${installmentCount}x. `
                        : ''}
                      O máximo permitido pela configuração de pagamento é {maxInstallments}x.
                      {Number(installmentCount) > maxInstallments
                        ? ' Reduza a quantidade de parcelas para continuar.'
                        : ''}
                    </p>
                  )}
                </div>

                {hasEntrance && (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <label
                        className="text-xs font-medium text-gray-700"
                        htmlFor={downPaymentByBankSlipId}
                      >
                        Pagamento da entrada com boleto
                      </label>
                      <Switch
                        id={downPaymentByBankSlipId}
                        checked={downPaymentByBankSlip}
                        onCheckedChange={(v) => {
                          setDownPaymentByBankSlip(v);
                          if (v) {
                            setProrrogarEntrada(false);
                            setProrrogarDate(null);
                          }
                          markDirty();
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <label
                        className="text-xs font-medium text-gray-700"
                        htmlFor={prorrogarEntradaId}
                      >
                        Prorrogar entrada
                      </label>
                      <Switch
                        id={prorrogarEntradaId}
                        checked={prorrogarEntrada}
                        onCheckedChange={(v) => {
                          setProrrogarEntrada(v);
                          if (v) setDownPaymentByBankSlip(false);
                          if (!v) setProrrogarDate(null);
                          markDirty();
                        }}
                      />
                    </div>

                    {prorrogarEntrada && (
                      <div className="space-y-1.5">
                        <DatePickerInput
                          id={prorrogarDateId}
                          label="Data da entrada prorrogada"
                          value={prorrogarDate ?? undefined}
                          required
                          aria-describedby={prorrogarDateErrorId}
                          aria-invalid={hasInvalidProrrogarDate}
                          onChange={(d) => {
                            setProrrogarDate(d ?? null);
                            markDirty();
                          }}
                        />
                        {hasInvalidProrrogarDate && (
                          <p id={prorrogarDateErrorId} className="text-xs text-destructive">
                            Informe uma data futura válida
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {paymentType === 'CASH' && (
              <div className="flex items-center justify-between gap-3">
                <label className="text-xs font-medium text-gray-700" htmlFor={boletoAvistaId}>
                  Pagamento com boleto
                </label>
                <Switch
                  id={boletoAvistaId}
                  checked={boletoAvista}
                  onCheckedChange={(value) => {
                    setBoletoAvista(value);
                    markDirty();
                  }}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-700">
                {isPickup ? 'Frete — retirada na BWS' : 'Frete'}
              </span>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                  R$
                </span>
                <Input
                  className="h-8 pl-9 text-xs"
                  disabled={isPickup}
                  value={
                    isPickup ? '0,00' : freightDigits ? formatCurrencyInput(freightDigits) : ''
                  }
                  onChange={(e) => {
                    setFreightDigits(e.target.value.replace(/\D/g, ''));
                    markDirty();
                  }}
                  placeholder="0,00"
                />
              </div>
            </div>

            {hasAnyItemSection && (
              <>
                <Separator className="my-1" />

                <div className="space-y-1.5">
                  <SectionTitle>Itens</SectionTitle>

                  {hasProducts && breakdown && (
                    <SummaryRow
                      label="Produtos"
                      value={fmtCents(cartSummaryCategories.products.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.products.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasAccessories && breakdown && (
                    <SummaryRow
                      label="Acessórios"
                      value={fmtCents(cartSummaryCategories.accessories.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.accessories.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasConnectivity && breakdown && (
                    <SummaryRow
                      label="Conectividade"
                      value={fmtCents(cartSummaryCategories.connectivity.netInCents)}
                      subtitle={
                        <>
                          {'Mensal recorrente'}
                          {renderDiscountTag(cartSummaryCategories.connectivity.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasMonthlyServices && breakdown && (
                    <SummaryRow
                      label="Serviços mensais"
                      value={fmtCents(cartSummaryCategories.monthlyServices.grossInCents)}
                      subtitle={
                        <>
                          {'Mensal recorrente'}
                          {renderDiscountTag(cartSummaryCategories.monthlyServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasYearlyServices && breakdown && (
                    <SummaryRow
                      label="Serviços anuais"
                      value={fmtCents(cartSummaryCategories.yearlyServices.grossInCents)}
                      subtitle={
                        <>
                          {'Anual recorrente'}
                          {renderDiscountTag(cartSummaryCategories.yearlyServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasFixedServices && breakdown && (
                    <SummaryRow
                      label="Serviços fixos"
                      value={fmtCents(cartSummaryCategories.fixedServices.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.fixedServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasFreight && (
                    <SummaryRow
                      label="Frete"
                      value={fmtCents(freightDisplayValue)}
                      subtitle={
                        paymentType === 'CASH'
                          ? 'À vista'
                          : freightOnEntrance
                            ? 'Entrada'
                            : 'Parcelamento'
                      }
                    />
                  )}

                  {totalDiscountInCents > 0 && (
                    <SummaryRow label="Desconto" value={fmtCents(totalDiscountInCents)} green />
                  )}
                </div>
              </>
            )}

            {showTotalAVista && summary.cashTotal && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total à vista</SectionTitle>
                  <SummaryRow
                    label={boletoAvista ? 'Pagamento por boleto' : 'Pagamento à vista'}
                    value={fmtCents(summary.cashTotal.totalInCents)}
                  />
                  {totalDiscountInCents > 0 && (
                    <SummaryRow
                      label="Total de descontos"
                      value={fmtCents(totalDiscountInCents)}
                      green
                    />
                  )}
                  <SummaryRow label="Total" value={fmtCents(summary.cashTotal.totalInCents)} bold />
                </div>
              </>
            )}

            {showTotalParcelado && summary.installmentTotal && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Parcelado</SectionTitle>
                  {summary.installmentTotal.downPaymentInCents > 0 && (
                    <SummaryRow
                      label={
                        hasFreight && freightOnEntrance
                          ? 'Entrada + Frete'
                          : summary.installmentTotal.downPaymentLabel
                      }
                      value={fmtCents(summary.installmentTotal.downPaymentInCents)}
                    />
                  )}
                  {isFreightInInstallments && (
                    <SummaryRow
                      label="Parcelado com frete"
                      value={fmtCents(parceladoComFreteInCents)}
                    />
                  )}
                  {summary.installmentTotal.installmentBreakdown && (
                    <SummaryRow
                      label={`Boleto (${summary.installmentTotal.installments}x)`}
                      value={fmtCents(
                        summary.installmentTotal.installmentBreakdown.installmentInCents,
                      )}
                    />
                  )}
                  {totalDiscountInCents > 0 && (
                    <SummaryRow
                      label="Total de descontos"
                      value={fmtCents(totalDiscountInCents)}
                      green
                    />
                  )}
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.installmentTotal.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringConnectivity && summary?.recurringConnectivity && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (SIM Card)</SectionTitle>
                  <SummaryRow
                    label="Boleto (mensal)"
                    value={fmtCents(summary.recurringConnectivity.monthlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringConnectivity.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringMonthlyServices && summary?.recurringMonthlyServices && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (Serviços mensais)</SectionTitle>
                  <SummaryRow
                    label="Boleto (mensal)"
                    value={fmtCents(summary.recurringMonthlyServices.monthlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringMonthlyServices.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringYearlyServices && summary?.recurringYearlyServices && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (Serviços anuais)</SectionTitle>
                  <SummaryRow
                    label="Boleto (anual)"
                    value={fmtCents(summary.recurringYearlyServices.yearlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringYearlyServices.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
